import { useEffect, useState, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { supabase } from "../supabaseClient";

// ------------------
// Localizer (stable reference)
const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

export default function AthleteCalendar({ userId, programId, onEventSelect, onEventsLoaded }) {
  const [events, setEvents] = useState([]);
  const [selectedWorkoutDrills, setSelectedWorkoutDrills] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filter State: Default to false (only showing upcoming) for list view
  const [showAllEvents, setShowAllEvents] = useState(false);

  // Persist current view & date to avoid toolbar breaking
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (!userId || !programId) return;
    let mounted = true;
    setLoading(true);

    async function load() {
      try {
        const allEvents = [];

        // -------- SC Workouts ----------
        const { data: scDrills } = await supabase
          .from("daily_exercise_drills")
          .select("*")
          .eq("user_id", userId)
          .eq("followed_program", programId)
          .order("drill_date", { ascending: true })
          .order("order_index", { ascending: true });

        if (scDrills?.length) {
          const grouped = scDrills.reduce((acc, drill) => {
            acc[drill.drill_date] = acc[drill.drill_date] || [];
            acc[drill.drill_date].push(drill);
            return acc;
          }, {});

          Object.entries(grouped).forEach(([date, drills]) => {
            const start = new Date(`${date}T08:00:00`);
            const end = new Date(start.getTime() + 60 * 60 * 1000);

            allEvents.push({
              id: `sc-${date}`,
              title: "SC Workout",
              start,
              end,
              raw: drills,
              color: "#2563eb",
              kind: "sc_workout",
            });
          });
        }

        // -------- Skill Workouts ----------
        const { data: skillWorkouts } = await supabase
          .from("skill_workouts")
          .select("*")
          .eq("athlete_id", userId)
          .eq("program_id", programId)
          .order("workout_date", { ascending: true });

        if (skillWorkouts?.length) {
          const workoutIds = skillWorkouts.map((w) => w.id);
          const { data: workoutDrills } = await supabase
            .from("workout_drills")
            .select("*")
            .in("workout_id", workoutIds);

          const drillsByWorkout = {};
          (workoutDrills || []).forEach((d) => {
            if (!drillsByWorkout[d.workout_id]) drillsByWorkout[d.workout_id] = [];
            drillsByWorkout[d.workout_id].push(d);
          });

          skillWorkouts.forEach((w) => {
            const start = new Date(`${w.workout_date}T08:00:00`);
            const end = new Date(start.getTime() + 60 * 60 * 1000);

            allEvents.push({
              id: `skill-${w.id}`,
              title: w.workout_name || "Skill Workout",
              start,
              end,
              raw: drillsByWorkout[w.id] || [],
              color: "#059669",
              kind: "skill_workout",
            });
          });
        }

        // -------- Trainer Sessions ----------
        const { data: trainerBookings } = await supabase
          .from("trainer_bookings")
          .select("*")
          .eq("athlete_id", userId)
          .order("start_at", { ascending: true });

        if (trainerBookings?.length) {
          trainerBookings.forEach((booking) => {
            allEvents.push({
              id: `trainer-${booking.id}`,
              title: booking.status === "pending" ? "Trainer Session (Pending)" : "Trainer Session",
              start: new Date(booking.start_at),
              end: new Date(booking.end_at),
              raw: booking,
              color: booking.status === "approved" ? "#7c3aed" : "#f59e0b",
              kind: "trainer_session",
            });
          });
        }

        if (mounted) {
          setEvents(allEvents);
          onEventsLoaded?.(allEvents);
        }
      } catch (err) {
        console.error("Calendar fetch error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => (mounted = false);
  }, [userId, programId]);

  // Filter & Sort Logic
  const visibleEvents = useMemo(() => {
    let processed = [...events];

    // 1. Filter if checkbox is unchecked
    if (!showAllEvents) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      processed = processed.filter((e) => new Date(e.start) >= today);
    }

    // 2. Sort chronologically
    return processed.sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [events, showAllEvents]);

  const handleEventSelect = (event) => {
    if (event.kind === "trainer_session") {
      setSelectedWorkoutDrills([]);
      setSelectedSession({
        title: event.title || "Trainer Session",
        start: event.start,
        end: event.end,
        status: event.raw?.status,
        location: event.raw?.location_address || "Location TBD",
      });
    } else {
      setSelectedSession(null);
      setSelectedWorkoutDrills(event.raw || []);
    }
    setModalOpen(true);
    onEventSelect?.(event);
  };

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.color || "#3b82f6",
      borderRadius: "8px",
      border: "none",
      color: "white",
      paddingLeft: 8,
    },
  });

  const formatReps = (value) => {
    if (Array.isArray(value)) {
      const cleaned = value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned.join(", ") : "N/A";
    }
    const raw = String(value ?? "").trim();
    return raw || "N/A";
  };

  const formatDuration = (drill) => {
    const numericDuration = Number(drill?.duration);
    if (Number.isFinite(numericDuration) && numericDuration > 0) return `${numericDuration} min`;
    const rawDuration = String(drill?.duration_or_reps ?? "").trim();
    return rawDuration || "N/A";
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4 border min-h-[500px]">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold">Calendar</h3>
          {loading && <span className="text-sm text-gray-500 animate-pulse">Loading…</span>}
        </div>

        {/* Checkbox Filter (List View Only) */}
        <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer select-none bg-gray-50 px-3 py-1.5 rounded-lg border hover:bg-gray-100 transition-colors w-fit md:hidden">
          <input
            type="checkbox"
            checked={showAllEvents}
            onChange={(e) => setShowAllEvents(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span>Show past events</span>
        </label>
      </div>

      {/* ---------------- DESKTOP VIEW (Big Calendar) ---------------- */}
      <div className="hidden md:block h-[720px]">
        <Calendar
          localizer={localizer}
          events={events} // Always show past events on calendar view
          startAccessor="start"
          endAccessor="end"
          views={["month", "week", "day"]}
          view={view}
          date={currentDate}
          onNavigate={setCurrentDate}
          onView={setView}
          selectable={false}
          onSelectEvent={handleEventSelect}
          eventPropGetter={eventPropGetter}
        />
      </div>

      {/* ---------------- MOBILE VIEW (List Cards) ---------------- */}
      <div className="md:hidden flex flex-col gap-3">
        {visibleEvents.length === 0 && !loading ? (
          <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
            {showAllEvents 
              ? "No workouts found." 
              : "No upcoming workouts scheduled."}
          </div>
        ) : (
          visibleEvents.map((event) => {
            const startDate = new Date(event.start);
            const isPast = startDate < new Date().setHours(0, 0, 0, 0);

            return (
              <div
                key={event.id}
                onClick={() => handleEventSelect(event)}
                className={`
                  relative flex flex-col p-4 rounded-xl border shadow-sm transition-all active:scale-95 cursor-pointer
                  ${isPast ? "bg-gray-50 border-gray-200 opacity-80" : "bg-white border-gray-200 hover:border-blue-300"}
                `}
              >
                {/* Color Strip Indicator */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-xl"
                  style={{ backgroundColor: event.color }}
                />

                <div className="pl-3 flex items-start justify-between">
                  <div>
                    <h4 className="font-bold text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {event.kind === "sc_workout" ? "Strength & Conditioning" : "Skill Training"}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {event.raw?.length || 0} Drills
                      </span>
                      {isPast && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                          Completed
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Date Badge */}
                  <div className="flex flex-col items-center justify-center bg-gray-100 rounded-lg px-3 py-2 min-w-[60px]">
                    <span className="text-xs font-bold text-gray-500 uppercase">
                      {format(startDate, "MMM")}
                    </span>
                    <span className="text-xl font-bold text-gray-800">
                      {format(startDate, "d")}
                    </span>
                    <span className="text-xs text-gray-400">
                      {format(startDate, "EEE")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ---------------- SHARED MODAL ---------------- */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-lg max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Workout Drills</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">
                ✕
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto flex-1 pr-1">
              {selectedSession ? (
                <div className="border p-4 rounded-lg bg-gray-50 space-y-2">
                  <p className="font-semibold text-gray-900">{selectedSession.title}</p>
                  <p className="text-sm text-gray-600">
                    {format(selectedSession.start, "MMM d, h:mm a")} - {format(selectedSession.end, "h:mm a")}
                  </p>
                  <p className="text-sm text-gray-600">Status: {selectedSession.status || "pending"}</p>
                  <p className="text-sm text-gray-600">Location: {selectedSession.location}</p>
                </div>
              ) : selectedWorkoutDrills.length === 0 ? (
                <p className="text-gray-500">No drills details found.</p>
              ) : (
                selectedWorkoutDrills.map((d, i) => (
                  <div key={d.id || i} className="border p-4 rounded-lg bg-gray-50">
                    <p className="font-semibold text-gray-900">{d.drill_name || "Drill"}</p>
                    <p className="text-gray-600 text-sm mt-2">{d.description || "No description provided."}</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div className="bg-white border rounded px-2 py-1 text-gray-700">
                        <span className="font-semibold">Duration:</span> {formatDuration(d)}
                      </div>
                      <div className="bg-white border rounded px-2 py-1 text-gray-700">
                        <span className="font-semibold">Sets:</span> {Number(d?.sets) > 0 ? d.sets : "N/A"}
                      </div>
                      <div className="bg-white border rounded px-2 py-1 text-gray-700">
                        <span className="font-semibold">Reps:</span> {formatReps(d?.reps)}
                      </div>
                    </div>
                    {d.notes && (
                      <p className="text-gray-500 text-sm mt-2 bg-yellow-50 p-2 rounded border border-yellow-100">
                        Notes: {d.notes}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={() => setModalOpen(false)}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
